// Client-side mirror of server/helpers/roundSchedule.js. CRA cannot import the
// CommonJS server helper, so the validation rules are kept in step by hand:
// the wizard, the review summary, the event page and the exports all use this
// module.

import { v4 as uuidv4 } from 'uuid';

export const MAX_ROUND = 200;
const MIN_YEAR = 1900;
const MAX_YEAR = 2100;

// Knockout stages in draw order. Group rounds are numbered instead.
export const SCHEDULE_STAGES = [
  'Playoff',
  'Last 64',
  'Last 32',
  'Last 16',
  'Last 8',
  'Semifinal',
  'Final'
];
const STAGE_ORDER = new Map(SCHEDULE_STAGES.map((stage, index) => [stage, index]));

// Bracket round titles vary by generator ("Last16", "Quarter-finals",
// "Play-In", ...). Lower-case the label, strip everything non-alphanumeric and
// look it up here so every spelling maps to the same canonical stage.
const STAGE_ALIASES = {
  playoff: 'Playoff',
  playoffs: 'Playoff',
  playin: 'Playoff',
  last64: 'Last 64',
  roundof64: 'Last 64',
  r64: 'Last 64',
  last32: 'Last 32',
  roundof32: 'Last 32',
  r32: 'Last 32',
  last16: 'Last 16',
  roundof16: 'Last 16',
  r16: 'Last 16',
  last8: 'Last 8',
  roundof8: 'Last 8',
  quarterfinal: 'Last 8',
  quarterfinals: 'Last 8',
  qf: 'Last 8',
  semifinal: 'Semifinal',
  semifinals: 'Semifinal',
  sf: 'Semifinal',
  final: 'Final',
  finals: 'Final'
};

const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:$|[T ].*)/;
const TIME_PATTERN = /^(\d{1,2}):([0-5]\d)(?::[0-5]\d)?$/;

export const normalizeStage = (value) => {
  const key = String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

  return STAGE_ALIASES[key] || null;
};

export const isBlankScheduleRow = (row) =>
  !String(row?.date ?? '').trim() && !String(row?.startTime ?? '').trim();

export const createScheduleRow = (round = '') => ({
  id: uuidv4(),
  round: String(round ?? ''),
  stage: '',
  date: '',
  startTime: ''
});

export const createScheduleStageRow = (stage = '') => ({
  id: uuidv4(),
  round: '',
  stage: String(stage ?? ''),
  date: '',
  startTime: ''
});

export const scheduleRowsFromStored = (schedule) =>
  (Array.isArray(schedule) ? schedule : []).map((entry) => {
    const stage = String(entry?.stage ?? '').trim();

    return {
      id: uuidv4(),
      round: String(entry?.round ?? ''),
      // Keep an unrecognised stored stage raw so the editor can show and
      // repair it instead of silently turning the row into a different one.
      stage: stage ? normalizeStage(stage) || stage : '',
      date: String(entry?.date ?? ''),
      startTime: String(entry?.startTime ?? '')
    };
  });

export const seedScheduleRows = (rounds) => {
  const count = Math.min(
    MAX_ROUND,
    Math.max(0, Math.floor(Number(rounds) || 0))
  );

  return Array.from({ length: count }, (_, index) =>
    createScheduleRow(index + 1)
  );
};

// The next free round number, or null when the schedule is already at the cap.
export const nextScheduleRound = (rows) => {
  const highest = (Array.isArray(rows) ? rows : []).reduce((max, row) => {
    const round = Number(row?.round);
    return Number.isInteger(round) && round > max ? round : max;
  }, 0);

  return highest >= MAX_ROUND ? null : highest + 1;
};

// The next unused group round up to maxGroupRound (or the highest round
// already listed), then the first unused knockout stage. Returns a selection
// value ("round:3" / "stage:Last 16") or null when every option is used.
export const nextScheduleSelection = (rows, maxGroupRound) => {
  const list = Array.isArray(rows) ? rows : [];

  if (list.length >= MAX_ROUND) {
    return null;
  }

  const usedRounds = new Set();
  const usedStages = new Set();

  list.forEach((row) => {
    const round = Number(row?.round);
    if (String(row?.round ?? '').trim() !== '' && Number.isInteger(round)) {
      usedRounds.add(round);
    }

    const stage = normalizeStage(row?.stage);
    if (stage) {
      usedStages.add(stage);
    }
  });

  const highest = Array.from(usedRounds).reduce((max, round) => Math.max(max, round), 0);
  const limit = Math.min(
    MAX_ROUND,
    Math.max(0, Math.floor(Number(maxGroupRound) || 0), highest)
  );

  for (let round = 1; round <= limit; round += 1) {
    if (!usedRounds.has(round)) {
      return `round:${round}`;
    }
  }

  const stage = SCHEDULE_STAGES.find((candidate) => !usedStages.has(candidate));
  return stage ? `stage:${stage}` : null;
};

const normalizeRound = (value) => {
  const round =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : NaN;

  if (!Number.isInteger(round) || round < 1 || round > MAX_ROUND) {
    return { error: `round must be a whole number between 1 and ${MAX_ROUND}.` };
  }

  return { value: round };
};

const normalizeDate = (value) => {
  const text = String(value ?? '').trim();
  const match = DATE_PATTERN.exec(text);

  if (!match) {
    return { error: 'date must be YYYY-MM-DD.' };
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (year < MIN_YEAR || year > MAX_YEAR) {
    return { error: `date year must be between ${MIN_YEAR} and ${MAX_YEAR}.` };
  }

  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return { error: 'date must be a real calendar date.' };
  }

  return { value: `${match[1]}-${match[2]}-${match[3]}` };
};

const normalizeTime = (value) => {
  const text = String(value ?? '').trim();
  const match = TIME_PATTERN.exec(text);

  if (!match) {
    return { error: 'startTime must be 24-hour HH:mm.' };
  }

  const hour = Number(match[1]);
  if (hour > 23) {
    return { error: 'startTime must be 24-hour HH:mm.' };
  }

  return { value: `${String(hour).padStart(2, '0')}:${match[2]}` };
};

export const scheduleEntryLabel = (entry) => {
  const stage = String(entry?.stage ?? '').trim();
  if (stage) {
    return stage;
  }

  const round = String(entry?.round ?? '').trim();
  return round ? `Round ${round}` : '';
};

export const normalizeScheduleRows = (rows) => {
  const list = Array.isArray(rows) ? rows : [];

  if (list.length > MAX_ROUND) {
    return {
      roundSchedule: [],
      error: `Round times cannot have more than ${MAX_ROUND} entries.`
    };
  }

  const schedule = [];
  const seenEntries = new Set();

  for (let index = 0; index < list.length; index += 1) {
    const row = list[index] ?? {};
    const dateText = String(row.date ?? '').trim();
    const timeText = String(row.startTime ?? '').trim();

    if (!dateText && !timeText) {
      continue;
    }

    const label = scheduleEntryLabel(row) || `Row ${index + 1}`;

    if (!dateText) {
      return {
        roundSchedule: [],
        error: `${label} needs a date (YYYY-MM-DD).`
      };
    }

    if (!timeText) {
      return {
        roundSchedule: [],
        error: `${label} needs a start time (HH:mm).`
      };
    }

    const hasRound = String(row.round ?? '').trim() !== '';
    const hasStage = String(row.stage ?? '').trim() !== '';

    if (!hasRound && !hasStage) {
      return {
        roundSchedule: [],
        error: `${label} needs a round number or a knockout stage.`
      };
    }

    if (hasRound && hasStage) {
      return {
        roundSchedule: [],
        error: `${label} cannot have both a round number and a knockout stage.`
      };
    }

    const date = normalizeDate(dateText);
    if (date.error) {
      return { roundSchedule: [], error: `${label}: ${date.error}` };
    }

    const time = normalizeTime(timeText);
    if (time.error) {
      return { roundSchedule: [], error: `${label}: ${time.error}` };
    }

    let entry;
    let key;

    if (hasRound) {
      const round = normalizeRound(row.round);
      if (round.error) {
        return { roundSchedule: [], error: `${label}: ${round.error}` };
      }

      entry = { round: round.value, date: date.value, startTime: time.value };
      key = `round:${round.value}`;
    } else {
      const stage = normalizeStage(row.stage);
      if (!stage) {
        return {
          roundSchedule: [],
          error: `${label}: stage "${String(row.stage).trim()}" is not recognised. Use ${SCHEDULE_STAGES.join(', ')}.`
        };
      }

      entry = { stage, date: date.value, startTime: time.value };
      key = `stage:${stage}`;
    }

    if (seenEntries.has(key)) {
      const description = hasRound ? `round ${entry.round}` : `stage ${entry.stage}`;
      return {
        roundSchedule: [],
        error: `${label}: ${description} appears more than once.`
      };
    }

    seenEntries.add(key);
    schedule.push(entry);
  }

  schedule.sort(compareScheduleEntries);

  return { roundSchedule: schedule, error: null };
};

const roundOf = (entry) => {
  const text = String(entry?.round ?? '').trim();
  if (!text) {
    return null;
  }

  const round = Number(text);
  return Number.isInteger(round) ? round : null;
};

const stageOrderOf = (entry) => {
  const stage = normalizeStage(entry?.stage);
  return stage ? STAGE_ORDER.get(stage) : null;
};

export const compareScheduleEntries = (a, b) => {
  const aRound = roundOf(a);
  const bRound = roundOf(b);

  if (aRound !== null && bRound !== null) {
    return aRound - bRound;
  }

  if (aRound !== null) {
    return -1;
  }

  if (bRound !== null) {
    return 1;
  }

  const aOrder = stageOrderOf(a);
  const bOrder = stageOrderOf(b);

  if (aOrder !== null && bOrder !== null) {
    return aOrder - bOrder;
  }

  if (aOrder !== null) {
    return -1;
  }

  return bOrder !== null ? 1 : 0;
};

export const sortScheduleEntries = (schedule) =>
  (Array.isArray(schedule) ? [...schedule] : []).sort(compareScheduleEntries);

export const scheduleEntriesByRound = (schedule) => {
  const byRound = new Map();

  (Array.isArray(schedule) ? schedule : []).forEach((entry) => {
    const round = roundOf(entry);
    if (round !== null) {
      byRound.set(round, entry);
    }
  });

  return byRound;
};

export const findRoundScheduleEntry = (schedule, round) => {
  const wanted = Number(round);
  if (!Number.isInteger(wanted)) {
    return null;
  }

  return (
    (Array.isArray(schedule) ? schedule : []).find(
      (entry) => roundOf(entry) === wanted
    ) || null
  );
};

export const findStageScheduleEntry = (schedule, label) => {
  const stage = normalizeStage(label);
  if (!stage) {
    return null;
  }

  return (
    (Array.isArray(schedule) ? schedule : []).find(
      (entry) => normalizeStage(entry?.stage) === stage
    ) || null
  );
};

// Resolve a bracket round title to its schedule entry. Named titles match a
// knockout stage only; a numeric title ("Round 2" or "2") is the only form
// allowed to fall back to a group round, so "Last16" can never pick up Round 1.
export const findScheduleEntryForBracketRound = (schedule, label) => {
  if (!Array.isArray(schedule)) {
    return null;
  }

  if (normalizeStage(label)) {
    return findStageScheduleEntry(schedule, label);
  }

  const match = /^\s*(?:round\s*)?(\d+)\s*$/i.exec(String(label ?? ''));
  return match ? findRoundScheduleEntry(schedule, Number(match[1])) : null;
};

// Split the stored plain date rather than constructing a Date, so displaying a
// date can never shift it by a timezone.
export const formatScheduleDate = (value) => {
  const text = String(value ?? '').trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:$|[T ].*)/.exec(text);

  return match ? `${match[3]}/${match[2]}/${match[1]}` : text;
};

export const formatScheduleEntry = (entry) =>
  [
    formatScheduleDate(entry?.date),
    String(entry?.startTime ?? '').trim()
  ]
    .filter(Boolean)
    .join(' · ');

export const formatScheduleDateTime = (entry) =>
  [
    formatScheduleDate(entry?.date),
    String(entry?.startTime ?? '').trim()
  ]
    .filter(Boolean)
    .join(' ');

export const formatScheduleRoundHeading = (round, entry) => {
  const heading = `Round ${String(round ?? '').trim()}`;
  const dateTime = formatScheduleDateTime(entry);

  return dateTime ? `${heading} - ${dateTime}` : heading;
};
